import type { Task } from '../types/mockup'

export const rescheduleTask = (
  draggedTask: Task, 
  targetTask: Task,
  allTasks: Task[],
  dropPosition: 'before' | 'after' = 'after'
): Task[] => {
  if (draggedTask.isAnchor || targetTask.isAnchor) {
    console.warn('Cannot move anchor tasks')
    return allTasks
  }

  const updatedTasks = [...allTasks]
  const draggedIndex = updatedTasks.findIndex(t => t.id === draggedTask.id)
  const targetIndex = updatedTasks.findIndex(t => t.id === targetTask.id)

  if (draggedIndex === -1 || targetIndex === -1) return allTasks

  const newStartTime = dropPosition === 'before' 
    ? new Date(targetTask.startTime.getTime() - draggedTask.duration * 60000)
    : new Date(targetTask.endTime.getTime())

  const timeDiff = newStartTime.getTime() - draggedTask.startTime.getTime()

  updatedTasks[draggedIndex] = {
    ...draggedTask,
    startTime: newStartTime,
    endTime: new Date(newStartTime.getTime() + draggedTask.duration * 60000)
  }

  const dependentTasks = allTasks.filter(t => 
    t.dependencies?.includes(draggedTask.id)
  )

  dependentTasks.forEach(depTask => {
    const depIndex = updatedTasks.findIndex(t => t.id === depTask.id)
    if (depIndex !== -1 && !depTask.isAnchor) {
      updatedTasks[depIndex] = {
        ...updatedTasks[depIndex],
        startTime: new Date(updatedTasks[depIndex].startTime.getTime() + timeDiff),
        endTime: new Date(updatedTasks[depIndex].endTime.getTime() + timeDiff)
      }
    }
  })

  return updatedTasks.sort((a, b) => a.startTime.getTime() - b.startTime.getTime())
}

export const validateTaskMove = (
  draggedTask: Task,
  targetTask: Task,
  allTasks: Task[]
): { valid: boolean; reason?: string } => {
  if (draggedTask.isAnchor) {
    return { valid: false, reason: 'Anchor tasks cannot be moved' }
  }

  if (targetTask.isAnchor) {
    return { valid: false, reason: 'Cannot drop on anchor tasks' }
  }

  if (draggedTask.id === targetTask.id) {
    return { valid: false, reason: 'Cannot drop task on itself' }
  }

  if (targetTask.dependencies?.includes(draggedTask.id)) {
    return { valid: false, reason: 'Would create circular dependency' }
  }

  const hasCircularDep = checkCircularDependency(
    draggedTask.id,
    targetTask.id,
    allTasks
  )

  if (hasCircularDep) {
    return { valid: false, reason: 'Would create circular dependency' }
  }

  return { valid: true }
}

const checkCircularDependency = (
  taskId: string,
  targetId: string,
  allTasks: Task[]
): boolean => {
  const visited = new Set<string>()
  
  const hasCycle = (currentId: string): boolean => {
    if (visited.has(currentId)) return true
    if (currentId === taskId) return true
    
    visited.add(currentId)
    
    const task = allTasks.find(t => t.id === currentId)
    if (!task || !task.dependencies) return false
    
    for (const depId of task.dependencies) {
      if (hasCycle(depId)) return true
    }
    
    return false
  }
  
  return hasCycle(targetId)
}

export const getTaskConflicts = (
  task: Task,
  allTasks: Task[]
): Task[] => {
  return allTasks.filter(otherTask => {
    if (otherTask.id === task.id) return false
    
    const taskStart = task.startTime.getTime()
    const taskEnd = task.endTime.getTime()
    const otherStart = otherTask.startTime.getTime()
    const otherEnd = otherTask.endTime.getTime()
    
    return (
      (taskStart >= otherStart && taskStart < otherEnd) ||
      (taskEnd > otherStart && taskEnd <= otherEnd) ||
      (taskStart <= otherStart && taskEnd >= otherEnd)
    )
  })
}

export const suggestOptimalTime = (
  task: Task,
  allTasks: Task[],
  preferredTime?: Date
): Date => {
  const sortedTasks = [...allTasks]
    .filter(t => t.id !== task.id)
    .sort((a, b) => a.startTime.getTime() - b.startTime.getTime())

  const taskDuration = task.duration * 60000
  const bufferTime = 5 * 60000

  let bestTime = preferredTime || task.startTime
  let minConflicts = Number.MAX_VALUE

  for (let i = 0; i <= sortedTasks.length; i++) {
    let candidateTime: Date
    
    if (i === 0) {
      candidateTime = new Date(
        sortedTasks[0].startTime.getTime() - taskDuration - bufferTime
      )
    } else if (i === sortedTasks.length) {
      candidateTime = new Date(
        sortedTasks[i - 1].endTime.getTime() + bufferTime
      )
    } else {
      const gapStart = sortedTasks[i - 1].endTime.getTime() + bufferTime
      const gapEnd = sortedTasks[i].startTime.getTime() - bufferTime
      
      if (gapEnd - gapStart >= taskDuration) {
        candidateTime = new Date(gapStart)
      } else {
        continue
      }
    }

    const testTask = { ...task, startTime: candidateTime, endTime: new Date(candidateTime.getTime() + taskDuration) }
    const conflicts = getTaskConflicts(testTask, allTasks)
    
    if (conflicts.length < minConflicts) {
      minConflicts = conflicts.length
      bestTime = candidateTime
      
      if (minConflicts === 0) break
    }
  }

  return bestTime
}